import { useState, useEffect } from 'react';

interface AppointmentCalendarProps {
  onSelectDateTime: (date: Date, time: string) => void;
  selectedDate?: Date;
  selectedTime?: string;
}

// Mock available time slots - in production, this would come from an API
const AVAILABLE_TIME_SLOTS = [
  '9:00 AM',
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '1:00 PM',
  '2:00 PM',
  '3:00 PM',
  '4:00 PM',
  '5:00 PM',
  '6:00 PM',
];

// Mock booked slots - in production, this would come from an API
const getBookedSlots = (date: Date): string[] => {
  // Simulate some booked slots
  const dayOfWeek = date.getDay();
  if (dayOfWeek === 1) return ['10:00 AM', '2:00 PM', '4:00 PM']; // Monday
  if (dayOfWeek === 3) return ['9:00 AM', '12:00 PM', '5:00 PM']; // Wednesday
  if (dayOfWeek === 5) return ['11:00 AM', '1:00 PM', '3:00 PM']; // Friday
  return [];
};

export default function AppointmentCalendar({
  onSelectDateTime,
  selectedDate,
  selectedTime,
}: AppointmentCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [localSelectedDate, setLocalSelectedDate] = useState<Date | null>(selectedDate || null);
  const [localSelectedTime, setLocalSelectedTime] = useState<string | null>(selectedTime || null);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);

  // ----------------------------------------------------------------------
  // Update available time slots whenever the selected date changes.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (localSelectedDate) {
      const booked = getBookedSlots(localSelectedDate);
      const available = AVAILABLE_TIME_SLOTS.filter(
        (slot) => !booked.includes(slot)
      );
      setAvailableSlots(available);
    } else {
      setAvailableSlots([]);
    }
  }, [localSelectedDate]);

  // ----------------------------------------------------------------------
  // Helper: calculate month details.
  // ----------------------------------------------------------------------
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  // ----------------------------------------------------------------------
  // Helper: determine if a date can be selected.
  // ----------------------------------------------------------------------
  const isDateAvailable = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Only allow dates from today onwards
    if (date < today) return false;

    // Don't allow Sundays (0)
    if (date.getDay() === 0) return false;

    // Only allow dates within next 60 days
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 60);
    if (date > maxDate) return false;

    return true;
  };

  // ----------------------------------------------------------------------
  // Handlers
  // ----------------------------------------------------------------------
  const handleDateClick = (day: number) => {
    const { year, month } = getDaysInMonth(currentMonth);
    const date = new Date(year, month, day);

    if (!isDateAvailable(date)) return;

    setLocalSelectedDate(date);
    setLocalSelectedTime(null); // Reset time when date changes
  };

  const handleTimeClick = (time: string) => {
    if (!localSelectedDate) return;

    setLocalSelectedTime(time);
    onSelectDateTime(localSelectedDate, time);
  };

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  // ----------------------------------------------------------------------
  // Render helpers
  // ----------------------------------------------------------------------
  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(
    currentMonth
  );
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const renderCalendarDays = () => {
    const days: JSX.Element[] = [];

    // Empty cells before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-12">
          {/* intentionally empty */}
        </div>
      );
    }

    // Render each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isAvailable = isDateAvailable(date);
      const isSelected =
        localSelectedDate?.getDate() === day &&
        localSelectedDate?.getMonth() === month &&
        localSelectedDate?.getFullYear() === year;

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          disabled={!isAvailable}
          className={`h-12 rounded-lg text-sm font-light transition-all duration-200 cursor-pointer ${
            isSelected
              ? 'bg-gold text-white font-medium'
              : isAvailable
              ? 'bg-bone hover:bg-gold/20 text-charcoal hover:text-charcoal'
              : 'bg-transparent text-greige/30 cursor-not-allowed'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  // ----------------------------------------------------------------------
  // Component JSX
  // ----------------------------------------------------------------------
  return (
    <div className="space-y-8">
      {/* Calendar */}
      <div className="bg-bone rounded-2xl p-6">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={previousMonth}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gold/10 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl text-charcoal"></i>
          </button>

          <h3 className="text-xl font-light text-charcoal tracking-wide">
            {monthNames[month]} {year}
          </h3>

          <button
            onClick={nextMonth}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gold/10 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl text-charcoal"></i>
          </button>
        </div>

        {/* Day Labels */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div
              key={day}
              className="text-center text-xs font-light text-greige tracking-wide"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">{renderCalendarDays()}</div>

        {/* Legend */}
        <div className="flex items-center gap-6 mt-6 pt-6 border-t border-greige/20">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gold"></div>
            <span className="text-xs text-greige font-light">Selected</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-bone border border-greige/30"></div>
            <span className="text-xs text-greige font-light">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-transparent border border-greige/20"></div>
            <span className="text-xs text-greige font-light">Unavailable</span>
          </div>
        </div>
      </div>

      {/* Time Slots */}
      {localSelectedDate && (
        <div className="bg-bone rounded-2xl p-6">
          <h3 className="text-xl font-light text-charcoal mb-4 tracking-wide">
            Available Times for{' '}
            {localSelectedDate.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </h3>

          {availableSlots.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {availableSlots.map((time) => (
                <button
                  key={time}
                  onClick={() => handleTimeClick(time)}
                  className={`py-3 px-4 rounded-lg text-sm font-light transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    localSelectedTime === time
                      ? 'bg-gold text-white font-medium'
                      : 'bg-white hover:bg-gold/20 text-charcoal hover:text-charcoal border border-greige/20'
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-greige text-sm font-light">
              No available time slots for this date. Please select another
              date.
            </p>
          )}
        </div>
      )}

      {/* Selection Summary */}
      {localSelectedDate && localSelectedTime && (
        <div className="bg-gold/10 border border-gold/30 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-gold rounded-full flex-shrink-0">
              <i className="ri-check-line text-white text-xl"></i>
            </div>
            <div>
              <h4 className="text-lg font-light text-charcoal mb-1">
                Appointment Selected
              </h4>
              <p className="text-greige text-sm font-light">
                {localSelectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}{' '}
                at {localSelectedTime}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
